import type { Coord, Ship, Shot } from '@sea-wars/game-core';
import { SketchBoard } from '@sea-wars/ui';
import { useEffect, useState } from 'react';

/** Jeden kadr przykładu: stan planszy + podpis mówiący, co właśnie widać. */
export interface DemoStep {
  readonly caption: string;
  readonly fleet?: readonly Ship[];
  readonly shots?: readonly Shot[];
  readonly aim?: Coord | null;
  readonly recent?: readonly Coord[];
}

interface HelpDemoProps {
  readonly steps: readonly DemoStep[];
  /** Opis całego przykładu dla czytników ekranu (sama plansza to obrazek). */
  readonly label: string;
  readonly size?: number;
  readonly seed?: number;
}

/** Tempo kadrów — wolno, żeby dało się przeczytać podpis. */
const STEP_MS = 1800;

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Animowany przykład w pomocy — rysowany tym samym `SketchBoard` co prawdziwa gra, więc
 * wygląda dokładnie jak w partii i nie rozjedzie się z nią po zmianie stylu. Zapętla się
 * sam (z pauzą — ruchoma treść dłuższa niż 5 s musi dać się zatrzymać, WCAG 2.2.2); przy
 * „ogranicz ruch” w systemie nie startuje sam, tylko czeka na strzałki.
 */
export function HelpDemo({ steps, label, size = 6, seed = 7 }: HelpDemoProps) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const step = steps[index] ?? steps[0];

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % steps.length);
    }, STEP_MS);
    return () => window.clearInterval(timer);
  }, [playing, steps.length]);

  if (!step) return null;

  const go = (delta: number) => {
    setPlaying(false);
    setIndex((current) => (current + delta + steps.length) % steps.length);
  };

  return (
    <figure className="help-demo">
      <div className="help-demo__board">
        <SketchBoard
          size={size}
          seed={seed}
          fleet={step.fleet ?? null}
          shots={step.shots ?? []}
          aim={step.aim ?? null}
          recent={step.recent ?? []}
          ariaLabel={label}
        />
      </div>
      <figcaption className="help-demo__caption">
        <span className="help-demo__counter">
          {index + 1}/{steps.length}
        </span>{' '}
        {step.caption}
      </figcaption>
      <div className="help-demo__controls">
        <button
          type="button"
          className="btn btn--ghost btn--small help-demo__btn"
          aria-label="Poprzedni krok"
          onClick={() => go(-1)}
        >
          ◀
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small help-demo__btn"
          onClick={() => setPlaying((value) => !value)}
        >
          {playing ? 'Pauza' : 'Odtwórz'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small help-demo__btn"
          aria-label="Następny krok"
          onClick={() => go(1)}
        >
          ▶
        </button>
      </div>
    </figure>
  );
}
